import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogIn } from "lucide-react";
import { adminLogin } from "@/lib/api";

const formatDetail = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
};

export default function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await adminLogin(email, password);
      onSuccess(user);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-40 pb-24 flex justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="flex flex-col items-center mb-8">
          <img src="/khabrik-logo.png" alt="Khabrik" className="w-16 h-16 rounded-full" />
          <h1 className="font-display text-3xl text-white mt-5">Admin Login</h1>
          <p className="font-mono-meta text-[11px] text-slate-500 uppercase tracking-widest mt-2 flex items-center gap-1.5">
            <Lock size={11} /> Authorized editors only
          </p>
        </div>
        <form onSubmit={submit} className="bg-[#15151A] border border-white/10 p-6 space-y-4">
          <label className="block">
            <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">Email</span>
            <input
              data-testid="admin-login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@khabrik.news"
              className="w-full bg-[#0D0D10] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600"
            />
          </label>
          <label className="block">
            <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">Password</span>
            <input
              data-testid="admin-login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#0D0D10] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600"
            />
          </label>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#FF453A]">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-[#FFD400] text-[#0D0D10] px-6 py-3 text-sm font-bold hover:bg-[#E6BE00] transition-colors disabled:opacity-60"
          >
            <LogIn size={15} /> {busy ? "Logging in..." : "Login"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
